import { DefaultLoader } from "excalibur";
import { VISUALS } from "../config";

export class CustomLoader extends DefaultLoader {
    private backgroundImage: HTMLImageElement;
    private characterImage: HTMLImageElement;
    private playButtonReady: boolean = false;
    private userActionResolve: (() => void) | null = null;

    constructor() {
        super();

        // Load images directly for the loader
        this.backgroundImage = new Image();
        this.backgroundImage.src = '/img/background/background02.png';

        this.characterImage = new Image();
        this.characterImage.src = '/img/ui/introscreen_character.png';
    }

    /**
     * Override onUserAction to show a play button and wait for user interaction.
     * This is required to unlock the AudioContext in modern browsers.
     */
    override onUserAction(): Promise<void> {
        return new Promise((resolve) => {
            this.userActionResolve = resolve;
            this.playButtonReady = true;

            // Also listen for keyboard (Enter/Space) or touch
            const handleInteraction = () => {
                if (this.userActionResolve) {
                    this.userActionResolve();
                    this.userActionResolve = null;
                    this.playButtonReady = false;
                }
                document.removeEventListener('click', handleInteraction);
                document.removeEventListener('touchstart', handleInteraction);
                document.removeEventListener('keydown', handleKeyInteraction);
            };

            const handleKeyInteraction = (e: KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    handleInteraction();
                }
            };

            document.addEventListener('click', handleInteraction, { once: true });
            document.addEventListener('touchstart', handleInteraction, { once: true });
            document.addEventListener('keydown', handleKeyInteraction);
        });
    }

    override onDraw(ctx: CanvasRenderingContext2D) {
        const width = ctx.canvas.width;
        const height = ctx.canvas.height;

        // 1. Draw Background
        // If loaded, draw it to fill screen
        if (this.backgroundImage.complete && this.backgroundImage.naturalWidth > 0) {
            // Calculate formatting to preserving aspect ratio (cover strategy)
            const imageRatio = this.backgroundImage.naturalWidth / this.backgroundImage.naturalHeight;
            const screenRatio = width / height;

            let drawWidth = width;
            let drawHeight = height;
            let startX = 0;
            let startY = 0;

            if (screenRatio > imageRatio) {
                // Screen is wider than image (relative to height) -> fit width, crop top/bottom
                drawWidth = width;
                drawHeight = width / imageRatio;
                startY = (height - drawHeight) / 2;
            } else {
                // Screen is taller than image (relative to width) -> fit height, crop left/right
                drawHeight = height;
                drawWidth = height * imageRatio;
                startX = (width - drawWidth) / 2;
            }

            ctx.drawImage(this.backgroundImage, startX, startY, drawWidth, drawHeight);
        } else {
            // Fallback fill
            ctx.fillStyle = VISUALS.COLORS.BACKGROUND;
            ctx.fillRect(0, 0, width, height);
        }

        // 2. Draw Character (Logo replacement)
        // Scaled down, centered roughly where the logo would be
        if (this.characterImage.complete && this.characterImage.naturalWidth > 0) {
            const charRatio = this.characterImage.naturalWidth / this.characterImage.naturalHeight;
            // E.g. 25% of screen height
            const targetHeight = height * 0.4;
            const targetWidth = targetHeight * charRatio;

            const x = (width / 2) - (targetWidth / 2);
            const y = (height / 2) - (targetHeight / 2) - 50; // slightly up to make room for bar/text

            ctx.drawImage(this.characterImage, x, y, targetWidth, targetHeight);
        }

        // 3. Draw Title Text "Riley vs Virus"
        ctx.save();
        ctx.fillStyle = "white";
        ctx.textAlign = "center";

        // Main Title
        ctx.font = "bold 60px 'Press Start 2P', sans-serif"; // Using pixel font if available or fallback
        // Add shadow for better visibility
        ctx.shadowColor = "rgba(0,0,0,0.8)";
        ctx.shadowBlur = 4;
        ctx.shadowOffsetX = 4;
        ctx.shadowOffsetY = 4;

        ctx.fillText("Riley vs Virus", width / 2, height - (height * 0.25));
        ctx.restore();

        // 4. Draw Progress Bar (only when loading)
        if (!this.playButtonReady) {
            const barWidth = width * 0.6;
            const barHeight = 20;
            const barX = (width - barWidth) / 2;
            const barY = height - (height * 0.15);

            // Bar container
            ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
            ctx.fillRect(barX, barY, barWidth, barHeight);

            // Bar progress
            ctx.fillStyle = "#4CC9F0"; // Cyan-ish color matching UI theme
            ctx.fillRect(barX, barY, barWidth * this.progress, barHeight);

            // Border
            ctx.strokeStyle = "white";
            ctx.lineWidth = 2;
            ctx.strokeRect(barX, barY, barWidth, barHeight);

            // Loading text
            ctx.save();
            ctx.fillStyle = "white";
            ctx.textAlign = "center";
            ctx.font = "20px 'Courier New', monospace";
            ctx.shadowColor = "rgba(0,0,0,0.8)";
            ctx.shadowBlur = 2;
            ctx.shadowOffsetX = 2;
            ctx.shadowOffsetY = 2;
            ctx.fillText(`Loading... ${Math.round(this.progress * 100)}%`, width / 2, barY + barHeight + 30);
            ctx.restore();
        } else {
            // 5. Draw PLAY Button when ready
            const buttonWidth = 200;
            const buttonHeight = 60;
            const buttonX = (width - buttonWidth) / 2;
            const buttonY = height - (height * 0.18);

            // Button background with pulsing effect
            const pulse = 0.8 + Math.sin(Date.now() / 300) * 0.2;
            ctx.fillStyle = `rgba(76, 201, 240, ${pulse})`;
            ctx.beginPath();
            ctx.roundRect(buttonX, buttonY, buttonWidth, buttonHeight, 10);
            ctx.fill();

            // Button border
            ctx.strokeStyle = "white";
            ctx.lineWidth = 3;
            ctx.stroke();

            // Button text
            ctx.save();
            ctx.fillStyle = "white";
            ctx.textAlign = "center";
            ctx.font = "bold 28px 'Courier New', monospace";
            ctx.shadowColor = "rgba(0,0,0,0.8)";
            ctx.shadowBlur = 2;
            ctx.shadowOffsetX = 2;
            ctx.shadowOffsetY = 2;
            ctx.fillText("▶ PLAY", width / 2, buttonY + buttonHeight / 2 + 10);
            ctx.restore();

            // Hint text
            ctx.save();
            ctx.fillStyle = "rgba(255, 255, 255, 0.7)";
            ctx.textAlign = "center";
            ctx.font = "14px 'Courier New', monospace";
            ctx.fillText("Click or tap to start", width / 2, buttonY + buttonHeight + 25);
            ctx.restore();
        }
    }
}
